// src/store/livestockSlice.js
// Endpoints derived from your existing livestock-action.js.
//
// Dependencies: @reduxjs/toolkit, axios, app-config (FARMDECK_API, TIME_LIMIT_FOR_FETCH), helpers/account (accountId).

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import Axios from 'axios'
import { FARMDECK_API, TIME_LIMIT_FOR_FETCH } from 'app-config/index.js'
import { accountId } from 'helpers/account.js'

// --- helpers ---
const baseUrl = tenant => FARMDECK_API.replace(':id', tenant ?? accountId)

const shouldRefetch = (lastFetch, force, cache) => {
    if (force) return true
    if (!cache) return true
    const age = Date.now() - (lastFetch || 0)
    const ttl = typeof TIME_LIMIT_FOR_FETCH === 'number' && TIME_LIMIT_FOR_FETCH > 0 ? TIME_LIMIT_FOR_FETCH : 5 * 60 * 1000
    return age >= ttl
}

/**
 * Every animal still held in a mob, active or archived, as the old (v1) animals: `{ id, nlisTag, individualTag, detail }`. A mob that cannot be read is skipped.
 * Plain async rather than a thunk: it is read once, for the Legacy Records migration, and kept out of the store.
 */
export async function fetchLegacyAnimals({ tenant, concurrency = 4 } = {}) {
    const url = baseUrl(tenant)
    const lists = await Promise.all(
        ['livestock/mobs/', 'livestock/archive/mobs/'].map(path =>
            Axios.get(`${url}${path}`)
                .then(res => res.data?.items || [])
                .catch(() => []),
        ),
    )
    const mobIds = [
        ...new Set(
            lists
                .flat()
                .map(mob => mob?.id)
                .filter(id => id != null),
        ),
    ]
    const animals = []
    for (let i = 0; i < mobIds.length; i += concurrency) {
        const batch = await Promise.all(
            mobIds.slice(i, i + concurrency).map(id =>
                Axios.get(`${url}livestock/mobs/${id}/animals`)
                    .then(res => (Array.isArray(res.data) ? res.data : res.data?.items || []))
                    .catch(() => []),
            ),
        )
        animals.push(...batch.flat())
    }
    return animals
}

// --------------------- Thunks (CRUD-style names) ---------------------
// mobs (many)
export const fetchMobs = createAsyncThunk('livestock/fetchMobs', async ({ id, force = false, cache = false, tenant } = {}, { getState, rejectWithValue }) => {
    const { lastFetch, data } = getState().livestock.mobs
    if (!data?.items || shouldRefetch(lastFetch, force, cache)) {
        try {
            const url = `${baseUrl(tenant)}livestock/mobs`
            const res = await Axios.get(url)
            return res.data // expect { items, summary? }
        } catch (err) {
            return rejectWithValue(err?.response?.data || err.message || 'fetchMobs failed')
        }
    }
    // cached
    return { cached: true, ...(data || {}) }
})

export const fetchArchiveMobs = createAsyncThunk('livestock/fetchArchiveMobs', async ({ id, force = false, cache = false, tenant } = {}, { getState, rejectWithValue }) => {
    const { lastFetch, data } = getState().livestock.mobsArchive || { lastFetch: 0, data: null }
    if (!data?.items || shouldRefetch(lastFetch, force, cache)) {
        try {
            const url = `${baseUrl(tenant)}livestock/archive/mobs/`
            const res = await Axios.get(url)
            return res.data // { items }
        } catch (err) {
            return rejectWithValue(err?.response?.data || err.message || 'fetchArchiveMobs failed')
        }
    }
    return { cached: true, ...(data || {}) }
})

export const updateBulkMobs = createAsyncThunk('livestock/updateBulkMobs', async ({ data, tenant } = {}, { rejectWithValue }) => {
    try {
        const url = `${baseUrl(tenant)}livestock/mobs/merge`
        const res = await Axios.post(url, data)
        return res.data // merged result
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'updateBulkMobs failed')
    }
})

// mob (single CRUD)
export const createMob = createAsyncThunk('livestock/createMob', async ({ data, tenant } = {}, { rejectWithValue }) => {
    try {
        const url = `${baseUrl(tenant)}livestock/mobs`
        const res = await Axios.post(url, data)
        return res.data
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'createMob failed')
    }
})

export const fetchMob = createAsyncThunk('livestock/fetchMob', async ({ data, payload, tenant } = {}, { rejectWithValue }) => {
    try {
        const id = data?.id ?? payload?.id
        const url = `${baseUrl(tenant)}livestock/mobs/${id}`
        const res = await Axios.get(url)
        return res.data
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'fetchMob failed')
    }
})

export const updateMob = createAsyncThunk('livestock/updateMob', async ({ data, tenant } = {}, { dispatch, signal, rejectWithValue }) => {
    try {
        const url = `${baseUrl(tenant)}livestock/mobs/${data.id}`
        const res = await Axios.put(url, data, { signal })
        const updated = res?.data

        // Always refresh list: server adds dynamic fields
        try {
            await dispatch(fetchMobs({ tenant, force: true, reason: 'afterUpdate' })).unwrap()
        } catch (e) {
            // log if you like, but don’t fail the PUT flow
        }

        // Then refresh the specific mob (animals[]/records[] may be stale/missing)
        try {
            await dispatch(fetchMob({ data: { id: data.id }, tenant, force: true, reason: 'afterUpdate' })).unwrap()
        } catch (e) {
            // likewise: don’t reject the update because a refetch hiccupped
        }

        return updated
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'updateMob failed')
    }
})

export const deleteMob = createAsyncThunk('livestock/deleteMob', async ({ data, payload, tenant } = {}, { rejectWithValue }) => {
    try {
        const id = data?.id ?? payload?.id
        const url = `${baseUrl(tenant)}livestock/mobs/${id}`
        const res = await Axios.delete(url)
        return { id, ...(res.data || {}) }
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'deleteMob failed')
    }
})

// animals (many)
export const fetchUnallocatedAnimals = createAsyncThunk('livestock/fetchUnallocatedAnimals', async ({ id, force = false, cache = false, tenant } = {}, { getState, rejectWithValue }) => {
    const { lastFetch, data } = getState().livestock.animals
    if (!data?.items || shouldRefetch(lastFetch, force, cache)) {
        try {
            const url = `${baseUrl(tenant)}livestock/animals`
            const res = await Axios.get(url)
            return res.data // { items }
        } catch (err) {
            return rejectWithValue(err?.response?.data || err.message || 'fetchUnallocatedAnimals failed')
        }
    }
    return { cached: true, ...(data || {}) }
})

export const updateBulkAnimals = createAsyncThunk('livestock/animals/', async ({ data, tenant, mobId } = {}, { signal, dispatch, rejectWithValue }) => {
    try {
        const url = `${baseUrl(tenant)}livestock/animals/`
        const res = await Axios.post(url, data)
        // Then refresh the specific mob (animals[]/records[] may be stale/missing)
        try {
            if (mobId && mobId) {
                await dispatch(fetchMob({ data: { id: mobId }, tenant, force: true, reason: 'afterUpdate' })).unwrap()
            }
        } catch (e) {
            // likewise: don’t reject the update because a refetch hiccupped
        }
        console.log('updateBulkAnimals res', res)
        return res.data // updated items
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'updateBulkAnimals failed')
    }
})

// animal (single)
// thunk
export const fetchAnimal = createAsyncThunk('livestock/fetchAnimal', async ({ id, data, payload, tenant } = {}, { dispatch, signal, rejectWithValue }) => {
    try {
        const sid = id ?? data?.id ?? payload?.id
        const sidStr = String(sid)
        const isVirtual = sidStr.includes('*')
        const mobId = data?.mobId ?? payload?.mobId ?? (isVirtual ? Number(sidStr.split('*')[0]) : undefined)

        let mob = null
        if (isVirtual && mobId) {
            mob = await dispatch(fetchMob({ data: { id: mobId }, tenant, force: true })).unwrap()
        }

        const url = `${baseUrl(tenant)}livestock/animals/${sid}`
        const res = await Axios.get(url, { signal })
        return { animal: res.data, mob } // hand the reducer everything it needs
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'fetchAnimal failed')
    }
})

export const updateAnimal = createAsyncThunk('livestock/updateAnimal', async ({ data, tenant } = {}, { dispatch, signal, rejectWithValue }) => {
    try {
        console.log('updateAnimal', data)
        const { id, items, mobId } = data || {}
        const res = await Axios.post(`${baseUrl(tenant)}livestock/animals`, { items: items, id: id }, { signal })
        const returned = res.data?.items?.[0]
        if (mobId) await dispatch(fetchMob({ data: { id: mobId }, tenant, force: true, reason: 'afterAnimalUpdate' })).unwrap()
        // If id changed (virtual → real), refetch the animal by its new id
        const rid = returned?.id ?? data.id
        const a = await dispatch(fetchAnimal({ id: rid, tenant })).unwrap()
        return a
    } catch (e) {
        return rejectWithValue(e?.response?.data || e.message || 'updateAnimal failed')
    }
})

// Commented out for now as this is non functional according to testing and documentaiton
// If u add it back, make sure to uncomment the builder below
// export const upsertAnimal = createAsyncThunk(
//     'livestock/upsertAnimal', // mirrors original POST updateAnimal
//     async ({ data, tenant } = {}, { rejectWithValue }) => {
//         try {
//             const url = `${baseUrl(tenant)}livestock/animals/`
//             const res = await Axios.post(url, data)
//             return res.data
//         } catch (err) {
//             return rejectWithValue(err?.response?.data || err.message || 'upsertAnimal failed')
//         }
//     },
// )

export const moveAnimal = createAsyncThunk('livestock/moveAnimal', async ({ data, tenant } = {}, { rejectWithValue }) => {
    try {
        // expects: data = { id: <mobId>, items: [...] }
        const url = `${baseUrl(tenant)}livestock/mobs/${data.id}/move`
        const res = await Axios.post(url, { items: data.items })
        return res.data
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'moveAnimal failed')
    }
})

export const moveMob = createAsyncThunk('livestock/moveMob', async ({ data, tenant } = {}, { rejectWithValue }) => {
    try {
        const url = `${baseUrl(tenant)}livestock/mobs/${data.id}/move`
        const res = await Axios.post(url, { items: data.items })
        return res.data
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'moveMob failed')
    }
})

export const deleteAnimal = createAsyncThunk('livestock/deleteAnimal', async ({ data, payload, tenant } = {}, { rejectWithValue }) => {
    try {
        const d = data ?? payload
        // Support both single and bulk delete based on payload shape
        if (Array.isArray(d?.items)) {
            const url = `${baseUrl(tenant)}livestock/animals/delete`
            const res = await Axios.post(url, d)
            return res.data // expects { items: [ids] } or similar
        } else {
            const id = d?.id
            const url = `${baseUrl(tenant)}livestock/animals/${id}`
            const res = await Axios.delete(url)
            return { id, ...(res.data || {}) }
        }
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'deleteAnimal failed')
    }
})

// notes (bulk)
export const deleteAnimalNotes = createAsyncThunk('livestock/deleteAnimalNotes', async ({ data, payload, tenant } = {}, { rejectWithValue }) => {
    try {
        const id = data?.id ?? payload?.id
        const items = data?.items ?? payload?.items
        const url = `${baseUrl(tenant)}livestock/animals/${id}/notes/delete`
        const res = await Axios.post(url, { items })
        return { id, ...res.data }
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'deleteAnimalNotes failed')
    }
})

export const deleteMobNotes = createAsyncThunk('livestock/deleteMobNotes', async ({ data, payload, tenant } = {}, { rejectWithValue }) => {
    try {
        const id = data?.id ?? payload?.id
        const items = data?.items ?? payload?.items
        const url = `${baseUrl(tenant)}livestock/mobs/${id}/notes/delete`
        const res = await Axios.post(url, { items })
        return { id, ...res.data }
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'deleteMobNotes failed')
    }
})

// note (single)
export const createAnimalNote = createAsyncThunk('livestock/createAnimalNote', async ({ data, payload, tenant } = {}, { rejectWithValue }) => {
    try {
        const id = data?.id ?? payload?.id
        const body = data?.data ?? payload?.data
        const url = `${baseUrl(tenant)}livestock/animals/${id}/notes/`
        const res = await Axios.post(url, body)
        return res.data
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'createAnimalNote failed')
    }
})

export const createMobNote = createAsyncThunk('livestock/createMobNote', async ({ data, payload, tenant } = {}, { rejectWithValue }) => {
    try {
        const id = data?.id ?? payload?.id
        const body = data?.data ?? payload?.data
        const url = `${baseUrl(tenant)}livestock/mobs/${id}/notes/`
        const res = await Axios.post(url, body)
        return res.data
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'createMobNote failed')
    }
})

export const deleteMobNote = createAsyncThunk('livestock/deleteMobNote', async ({ data, payload, tenant } = {}, { rejectWithValue }) => {
    try {
        const id = data?.id ?? payload?.id
        const rid = data?.rid ?? payload?.rid
        const url = `${baseUrl(tenant)}livestock/mobs/${id}/notes/${rid}`
        const res = await Axios.delete(url)
        return { id, rid, ...(res.data || {}) }
    } catch (err) {
        return rejectWithValue(err?.response?.data || err.message || 'deleteMobNote failed')
    }
})

// --------------------- Slice ---------------------
const leaf = () => ({
    lastFetch: 0,
    loading: false,
    data: null,
    error: null,
    lastError: 0,
})

// notes buckets keep local, normalised caches
const notesBucket = () => ({ items: [], lastFetch: 0, loading: false, error: null })

const initialState = {
    mobs: leaf(),
    mobsArchive: leaf(),
    mob: leaf(),
    animals: leaf(),
    animal: leaf(),
    notes: leaf(),
    note: leaf(),
    // normalised notes by entity
    animalNotes: {}, // { [animalId]: { items: Note[], lastFetch, loading, error } }
    mobNotes: {}, // { [mobId]: { items: Note[], lastFetch, loading, error } }
}

// helpers to ensure note buckets
const ensureAnimalNotes = (state, animalId) => {
    if (!state.animalNotes[animalId]) state.animalNotes[animalId] = notesBucket()
    return state.animalNotes[animalId]
}
const ensureMobNotes = (state, mobId) => {
    if (!state.mobNotes[mobId]) state.mobNotes[mobId] = notesBucket()
    return state.mobNotes[mobId]
}

const livestockSlice = createSlice({
    name: 'livestock',
    initialState,
    reducers: {},
    extraReducers: builder => {
        // fetchMobs
        builder.addCase(fetchMobs.pending, state => {
            state.mobs.loading = true
        })
        builder.addCase(fetchMobs.fulfilled, (state, action) => {
            state.mobs.loading = false
            // action.payload may be {cached: true, ...}
            state.mobs.data = action.payload
            state.mobs.lastFetch = Date.now()
            state.mobs.error = null
        })
        builder.addCase(fetchMobs.rejected, (state, action) => {
            state.mobs.loading = false
            state.mobs.error = action.payload || action.error?.message || 'fetchMobs error'
            state.mobs.lastError = Date.now()
        })

        // fetchArchiveMobs
        builder.addCase(fetchArchiveMobs.pending, s => {
            s.mobsArchive.loading = true
        })
        builder.addCase(fetchArchiveMobs.fulfilled, (s, a) => {
            s.mobsArchive.loading = false
            s.mobsArchive.data = a.payload
            s.mobsArchive.lastFetch = Date.now()
            s.mobsArchive.error = null
        })
        builder.addCase(fetchArchiveMobs.rejected, (s, a) => {
            s.mobsArchive.loading = false
            s.mobsArchive.error = a.payload || a.error?.message || 'fetchArchiveMobs error'
            s.mobsArchive.lastError = Date.now()
        })

        // updateBulkMobs
        builder.addCase(updateBulkMobs.pending, s => {
            s.mobs.loading = true
        })
        builder.addCase(updateBulkMobs.fulfilled, (s, a) => {
            s.mobs.loading = false
            // upsert into existing items if present
            const incoming = a.payload?.items || a.payload
            if (Array.isArray(incoming)) {
                const prev = s.mobs.data?.items || []
                const map = new Map(prev.map(x => [x.id, x]))
                for (const m of incoming) map.set(m.id, { ...(map.get(m.id) || {}), ...m })
                s.mobs.data = { ...(s.mobs.data || {}), items: Array.from(map.values()) }
            } else {
                s.mobs.data = a.payload
            }
            s.mobs.error = null
        })
        builder.addCase(updateBulkMobs.rejected, (s, a) => {
            s.mobs.loading = false
            s.mobs.error = a.payload || a.error?.message || 'updateBulkMobs error'
            s.mobs.lastError = Date.now()
        })

        // create/fetch/update/delete mob
        builder.addCase(createMob.fulfilled, (s, a) => {
            const item = a.payload
            const items = s.mobs.data?.items || []
            s.mobs.data = { ...(s.mobs.data || {}), items: [item, ...items] }
        })
        builder.addCase(fetchMob.fulfilled, (s, a) => {
            const raw = a.payload
            const item = normaliseMob(raw) // <- centralised shaping
            const items = s.mobs.data?.items ? [...s.mobs.data.items] : []
            const idx = items.findIndex(x => x.id === item.id)
            if (idx >= 0) items[idx] = { ...items[idx], ...item }
            else items.unshift(item)

            s.mobs.data = { ...(s.mobs.data || {}), items }
            s.mob.data = item
            s.mob.lastFetch = Date.now()
            s.mob.error = null
        })
        builder.addCase(updateMob.fulfilled, (s, a) => {
            const item = a.payload
            const items = s.mobs.data?.items || []
            const idx = items.findIndex(x => x.id === item.id)
            if (idx >= 0) items[idx] = { ...items[idx], ...item }
            else items.unshift(item)
            s.mobs.data = { ...(s.mobs.data || {}), items }
        })
        builder.addCase(deleteMob.fulfilled, (s, a) => {
            const id = a.payload?.id
            const items = (s.mobs.data?.items || []).filter(x => x.id !== id)
            s.mobs.data = { ...(s.mobs.data || {}), items }
        })

        // animals (list)
        builder.addCase(fetchUnallocatedAnimals.pending, s => {
            s.animals.loading = true
        })
        builder.addCase(fetchUnallocatedAnimals.fulfilled, (s, a) => {
            s.animals.loading = false
            s.animals.data = a.payload
            s.animals.lastFetch = Date.now()
            s.animals.error = null
        })
        builder.addCase(fetchUnallocatedAnimals.rejected, (s, a) => {
            s.animals.loading = false
            s.animals.error = a.payload || a.error?.message || 'fetchUnallocatedAnimals error'
            s.animals.lastError = Date.now()
        })

        builder.addCase(updateBulkAnimals.fulfilled, (s, a) => {
            const incoming = a.payload?.items || a.payload
            if (Array.isArray(incoming)) {
                const prev = s.animals.data?.items || []
                const map = new Map(prev.map(x => [x.id, x]))
                for (const m of incoming) map.set(m.id, { ...(map.get(m.id) || {}), ...m })
                s.animals.data = { ...(s.animals.data || {}), items: Array.from(map.values()) }
            } else {
                s.animals.data = a.payload
            }
        })

        // animal (single)
        builder.addCase(fetchAnimal.pending, s => {
            s.animal.loading = true
        })
        builder.addCase(fetchAnimal.fulfilled, (s, a) => {
            const { animal: raw, mob: mobFromThunk } = a.payload
            const isVirtual = String(raw?.id || '').includes('*')

            const mob = mobFromThunk || (isVirtual ? (s.mob?.data?.id === Number(String(raw.id).split('*')[0]) ? s.mob.data : null) : null) || null

            const animal = isVirtual && mob ? normaliseVirtualAnimal(raw, mob) : raw

            s.animal.loading = false
            s.animal.data = animal
            s.animal.lastFetch = Date.now()
            s.animal.error = null

            // Optional: keep mobs list in sync if you rely on it elsewhere
            if (mobFromThunk) {
                const items = s.mobs.data?.items ? [...s.mobs.data.items] : []
                const idx = items.findIndex(x => x.id === mobFromThunk.id)
                if (idx >= 0) items[idx] = { ...items[idx], ...mobFromThunk }
                else items.unshift(mobFromThunk)
                s.mobs.data = { ...(s.mobs.data || {}), items }
                // keep mob slice in sync too
                s.mob.data = mobFromThunk
                s.mob.lastFetch = Date.now()
                s.mob.error = null
            }
        })
        builder.addCase(fetchAnimal.rejected, (s, a) => {
            s.animal.loading = false
            s.animal.error = a.payload || a.error?.message || 'fetchAnimal error'
            s.animal.lastError = Date.now()
        })

        builder.addCase(updateAnimal.fulfilled, (s, a) => {
            // xxx
            // issue is that the payload is more than 1 animal and that the id might contain * which means its changed.
            // so we need to update the animal store and also the mobs store
            const data = a.payload || {}
            if (Array.isArray(data?.items)) {
                s.animal.data = data?.items?.[0]
            }
        })
        // builder.addCase(upsertAnimal.fulfilled, (s, a) => {
        //     s.animal.data = a.payload
        // })
        builder.addCase(moveAnimal.fulfilled, (s, a) => {
            // server returns updated animals; update animals list if present
            const incoming = a.payload?.items || []
            if (s.animals.data?.items && Array.isArray(incoming)) {
                const map = new Map(s.animals.data.items.map(x => [x.id, x]))
                for (const m of incoming) map.set(m.id, { ...(map.get(m.id) || {}), ...m })
                s.animals.data.items = Array.from(map.values())
            }
        })
        builder.addCase(deleteAnimal.fulfilled, (s, a) => {
            // If bulk, payload may contain list; if single, payload has id
            const ids = a.payload?.items || (a.payload?.id ? [a.payload.id] : [])
            if (s.animals.data?.items && ids.length) {
                s.animals.data.items = s.animals.data.items.filter(x => !ids.includes(x.id))
            }
            if (s.animal.data && ids.includes(s.animal.data.id)) {
                s.animal.data = null
            }
        })

        // notes (bulk)
        builder.addCase(deleteAnimalNotes.fulfilled, (s, a) => {
            const { id, items } = a.payload || {}
            if (id && Array.isArray(items) && s.notes.data?.[id]) {
                s.notes.data[id] = s.notes.data[id].filter(n => !items.includes(n.id))
            }
        })
        builder.addCase(deleteMobNotes.fulfilled, (s, a) => {
            const { id, items } = a.payload || {}
            if (id && Array.isArray(items) && s.notes.data?.[id]) {
                s.notes.data[id] = s.notes.data[id].filter(n => !items.includes(n.id))
            }
        })

        // note (single)
        builder.addCase(createAnimalNote.fulfilled, (s, a) => {
            const note = a.payload
            if (note?.entityId) {
                if (!s.notes.data) s.notes.data = {}
                if (!s.notes.data[note.entityId]) s.notes.data[note.entityId] = []
                s.notes.data[note.entityId].push(note)
            }
            s.note.data = note
        })
        builder.addCase(createMobNote.fulfilled, (s, a) => {
            const note = a.payload
            if (note?.entityId) {
                if (!s.notes.data) s.notes.data = {}
                if (!s.notes.data[note.entityId]) s.notes.data[note.entityId] = []
                s.notes.data[note.entityId].push(note)
            }
            s.note.data = note
        })
        builder.addCase(deleteMobNote.fulfilled, (s, a) => {
            const { id, rid } = a.payload || {}
            if (id && rid && s.notes.data?.[id]) {
                s.notes.data[id] = s.notes.data[id].filter(n => n.id !== rid)
            }
            s.note.data = null
        })
    },
})

export default livestockSlice.reducer

/** Selectors for notes */
export const selectAnimalNotes = (state, animalId) => state.livestock.animalNotes[animalId]?.items || []
export const selectMobNotes = (state, mobId) => state.livestock.mobNotes[mobId]?.items || []

// utils/normaliseMob.js (or inline)
const normaliseMob = mob => {
    if (!mob || typeof mob !== 'object') return mob
    const mobDetail = mob.detail || {}

    const animals = (mob.animals || []).map(a => {
        const idStr = String(a?.id ?? '')
        const isVirtual = idStr.includes('*')

        // Detail: for virtuals, front-load mob.detail then keep any explicit animal.detail overrides
        const mergedDetail = isVirtual ? { ...mobDetail, ...(a.detail || {}) } : a.detail || {}

        // paddock: alias origin → next if backend flips keys
        const paddockNextId = a.paddockNextId ?? a.paddockOriginId ?? null

        return {
            ...a,
            isVirtual,
            detail: mergedDetail,
            farmId: a.farmId ?? mob.farmId ?? null,
            paddockNextId,
            // keep original for debugging/telemetry, or drop it if you prefer:
            paddockOriginId: a.paddockOriginId,
        }
    })

    // Records: keep as-is; structure is backend-owned.
    // (If you later add richer typing, do it here.)
    const records = mob.records ? [...mob.records] : []

    return { ...mob, animals, records }
}
const normaliseVirtualAnimal = (animal, mob) => ({
    ...animal,
    isVirtual: true,
    farmId: animal.farmId ?? mob?.farmId ?? null,
    paddockNextId: animal.paddockNextId ?? animal.paddockOriginId ?? null,
    detail: { ...(mob?.detail || {}), ...(animal.detail || {}) },
})
